const getResponsiveStyles = (screenSize, theme, language) => {
  const { isMobile, isTablet } = screenSize;
  const isRTL = language === "ar";

  return {
    container: {
      padding: isMobile ? "12px" : isTablet ? "16px" : "24px",
      maxWidth: "100%",
      margin: "0 auto",
      minHeight: "100vh",
      // Matches (Admin)EmployeeActions' exact container gradient — the extra
      // third gradient stop this page used to have (#334155 dark / #cbd5e1
      // light) made it read as noticeably more "navy" than the rest of the
      // system; every other page uses this same 2-stop gradient.
      background:
        theme === "dark"
          ? "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)"
          : "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
      position: "relative",
      boxSizing: "border-box",
      direction: isRTL ? "rtl" : "ltr",
    },

    // Matches (Admin) Level_Configuration's WelcomeHeader style exactly — a
    // light, almost-invisible-background banner (near-zero-opacity gradient
    // in dark mode, soft slate border, no heavy shadow) instead of a fully
    // opaque dark card with a big drop shadow, which is what made this
    // page's header read as a solid navy block against the reference style.
    headerSection: {
      padding: isMobile ? "20px 16px" : isTablet ? "30px 24px" : "40px 32px",
      borderRadius: isMobile ? "16px" : "24px",
      marginBottom: isMobile ? "16px" : isTablet ? "24px" : "32px",
      marginTop: -40,
      backdropFilter: "blur(20px)",
      WebkitBackdropFilter: "blur(20px)",
      background:
        theme === "dark"
          ? "linear-gradient(135deg, rgba(30, 41, 59, 0) 0%, rgba(15, 23, 42, 0) 100%)"
          : "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%)",
      border:
        theme === "dark"
          ? "1px solid rgba(148, 163, 184, 0.15)"
          : "1px solid rgba(0, 0, 0, 0.05)",
      position: "relative",
      overflow: "hidden",
      transition: "all 0.3s ease",
    },

    headerContent: {
      display: "flex",
      flexDirection: isMobile ? "column" : "row",
      justifyContent: "space-between",
      alignItems: isMobile ? "flex-start" : "center",
      gap: isMobile ? "20px" : "32px",
      position: "relative",
      zIndex: 1,
    },

    headerLeft: {
      display: "flex",
      alignItems: "center",
      gap: isMobile ? "16px" : "32px",
      flex: 1,
    },

    headerTitle: {
      fontSize: isMobile ? "28px" : isTablet ? "36px" : "42px",
      fontWeight: "900",
      margin: "0 0 0px 0",
      color: theme === "dark" ? "#ffffff" : "#1a1a1a",
      letterSpacing: "-0.02em",
      lineHeight: "1.1",
      textShadow:
        theme === "dark"
          ? "0 2px 4px rgba(0,0,0,0.3)"
          : "0 2px 4px rgba(0,0,0,0.1)",
      textAlign: isRTL ? "right" : "left",
      marginBottom: 20,
    },

    headerText: {
      display: "flex",
      flexDirection: isMobile ? "column" : "row",
      alignItems: isMobile ? "flex-start" : "center",
      gap: isMobile ? "12px" : "18px",
      width: "100%",
    },

    headerSubtitle: {
      fontSize: isMobile ? "14px" : isTablet ? "16px" : "18px",
      color:
        theme === "dark" ? "rgba(255,255,255,0.8)" : "rgba(26,26,26,0.7)",
      margin: 0,
      lineHeight: "1.6",
      maxWidth: isMobile ? "100%" : "500px",
      textAlign: isRTL ? "right" : "left",
    },

    headerActions: {
      display: "flex",
      gap: isMobile ? "8px" : "16px",
      alignItems: "center",
      flexWrap: "wrap",
      justifyContent: isMobile ? "center" : "flex-end",
      width: isMobile ? "100%" : "auto",
      flexDirection: isRTL ? "row" : "row-reverse",
    },

    // Stat card styles removed — the Calendar stats now render through the
    // shared src/components/StatsGrid.jsx with the app-wide stat styles from
    // src/utils/styleUtils.js (see components/stats/StatsGrid.jsx).

    // Same WelcomeHeader-style treatment as headerSection above — used by
    // the "Tasks" panel (and reused by "Upcoming Events" via sidebarCard
    // below), so the whole page reads as one consistent light/transparent
    // look instead of the previous opaque dark cards with heavy shadows.
    controlPanel: {
      background:
        theme === "dark"
          ? "linear-gradient(135deg, rgba(30, 41, 59, 0) 0%, rgba(15, 23, 42, 0) 100%)"
          : "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%)",
      padding: isMobile ? "16px" : isTablet ? "24px" : "32px",
      borderRadius: isMobile ? "16px" : "24px",
      marginBottom: isMobile ? "16px" : isTablet ? "24px" : "32px",
      backdropFilter: "blur(20px)",
      WebkitBackdropFilter: "blur(20px)",
      border:
        theme === "dark"
          ? "1px solid rgba(148, 163, 184, 0.15)"
          : "1px solid rgba(0, 0, 0, 0.05)",
      transition: "all 0.3s ease",
    },

    controlRow: {
      display: "flex",
      flexDirection: isMobile ? "column" : "row",
      gap: isMobile ? "12px" : "20px",
      alignItems: isMobile ? "stretch" : "center",
      flexWrap: "wrap",
      marginBottom: isMobile ? "16px" : "24px",
    },

    searchContainer: {
      position: "relative",
      flex: isMobile ? "none" : 1,
      minWidth: isMobile ? "100%" : "320px",
      width: isMobile ? "100%" : "auto",
    },

    searchInput: {
      width: "100%",
      padding: isMobile ? "12px 16px 12px 48px" : "16px 20px 16px 56px",
      paddingLeft: isRTL ? "16px" : isMobile ? "48px" : "56px",
      paddingRight: isRTL ? (isMobile ? "48px" : "56px") : "16px",
      borderRadius: isMobile ? "12px" : "16px",
      border:
        theme === "dark"
          ? "2px solid rgba(255,255,255,0.12)"
          : "2px solid rgba(0,0,0,0.08)",
      background:
        theme === "dark" ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.9)",
      color: theme === "dark" ? "#fff" : "#1a1a1a",
      fontSize: isMobile ? "14px" : "15px",
      fontWeight: "500",
      outline: "none",
      transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
      backdropFilter: "blur(10px)",
      boxSizing: "border-box",
      textAlign: isRTL ? "right" : "left",
    },

    calendarContainer: {
      display: "grid",
      gridTemplateColumns: "1fr",
      width: "100%",
      gap: isMobile ? "16px" : isTablet ? "24px" : "32px",
      alignItems: "stretch",
      marginBottom: isMobile ? "40px" : isTablet ? "48px" : "64px",
    },

    // Same WelcomeHeader-style treatment as headerSection / controlPanel /
    // sidebarCard — the calendar grid's own wrapper card.
    calendarMain: {
      background:
        theme === "dark"
          ? "linear-gradient(135deg, rgba(30, 41, 59, 0) 0%, rgba(15, 23, 42, 0) 100%)"
          : "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%)",
      padding: isMobile ? "16px" : isTablet ? "24px" : "32px",
      borderRadius: isMobile ? "16px" : "24px",
      backdropFilter: "blur(20px)",
      WebkitBackdropFilter: "blur(20px)",
      border:
        theme === "dark"
          ? "1px solid rgba(148, 163, 184, 0.15)"
          : "1px solid rgba(0, 0, 0, 0.05)",
      transition: "all 0.3s ease",
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      gap: "16px",
      overflow: "hidden",
      boxSizing: "border-box",
    },

    calendarHeader: {
      display: "flex",
      flexDirection: isMobile ? "column" : "row",
      justifyContent: "space-between",
      alignItems: isMobile ? "center" : "center",
      gap: isMobile ? "16px" : "20px",
      marginBottom: isMobile ? "20px" : isTablet ? "24px" : "32px",
      paddingBottom: isMobile ? "16px" : isTablet ? "20px" : "24px",
      borderBottom:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.1)"
          : "1px solid rgba(0,0,0,0.08)",
    },

    calendarNavigation: {
      display: "flex",
      alignItems: "center",
      gap: isMobile ? "12px" : "20px",
      order: isMobile ? 1 : 0,
      flexDirection: isRTL ? "row-reverse" : "row",
    },

    navButton: {
      width: isMobile ? "40px" : "48px",
      height: isMobile ? "40px" : "48px",
      borderRadius: isMobile ? "10px" : "14px",
      border: "none",
      background:
        theme === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
      color: theme === "dark" ? "#fff" : "#1a1a1a",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
      backdropFilter: "blur(10px)",
      fontWeight: "600",
    },

    currentMonth: {
      fontSize: isMobile ? "18px" : isTablet ? "20px" : "24px",
      fontWeight: "800",
      color: theme === "dark" ? "#fff" : "#1a1a1a",
      minWidth: isMobile ? "200px" : "280px",
      textAlign: "center",
      letterSpacing: "-0.01em",
    },

    viewModeButtons: {
      display: "flex",
      gap: isMobile ? "4px" : "8px",
      background:
        theme === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)",
      borderRadius: isMobile ? "12px" : "16px",
      padding: isMobile ? "4px" : "6px",
      backdropFilter: "blur(10px)",
      order: isMobile ? 2 : 0,
    },

    viewModeButton: (isActive) => ({
      padding: isMobile ? "8px 12px" : "12px 20px",
      borderRadius: isMobile ? "8px" : "12px",
      border: "none",
      background: isActive
        ? "linear-gradient(135deg, #2e7cba, #2e7cba)"
        : "transparent",
      color: isActive
        ? "#fff"
        : theme === "dark"
        ? "rgba(255,255,255,0.7)"
        : "rgba(26,26,26,0.7)",
      fontSize: isMobile ? "12px" : "14px",
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
      textTransform: "uppercase",
      letterSpacing: "0.3px",
    }),

    calendarGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(7, 1fr)",
      gap: "10px",
      background:
        theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
      borderRadius: isMobile ? "8px" : "12px",
      overflow: "hidden",
      width: "100%",
    },

    calendarDayHeader: {
      background:
        theme === "dark" ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.98)",
      padding: isMobile ? "8px 4px" : "16px 8px",
      textAlign: "center",
      fontSize: isMobile ? "12px" : "14px",
      fontWeight: "600",
      color:
        theme === "dark" ? "rgba(255,255,255,0.8)" : "rgba(26,26,26,0.7)",
    },

    calendarDay: (dayObj, isSelected, isToday, isVacation, isInRange, getEventsForDate, dateRangeMode) => {
      const { date, isCurrentMonth } = dayObj;
      const hasEvents = getEventsForDate(date).length > 0;

      return {
        background: hasEvents
          ? "linear-gradient(135deg, #ef4444 60%, #dc2626 100%)"
          : isVacation
          ? "linear-gradient(135deg, #06b6d4 60%, #0891b2 100%)"
          : isSelected
          ? "linear-gradient(135deg, #2e7cba 60%, #2e7cba 100%)"
          : isToday
          ? "linear-gradient(135deg, #2e7cba 60%, #2e7cba 100%)"
          : isInRange
          ? "linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(99, 102, 241, 0.15))"
          : theme === "dark"
          ? "rgba(255,255,255,0.03)"
          : "rgba(255,255,255,0.85)",
        minHeight: isMobile ? "80px" : isTablet ? "100px" : "120px",
        padding: isMobile ? "8px 6px 6px 6px" : "14px 10px 10px 10px",
        position: "relative",
        cursor: "pointer",
        transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
        opacity: isCurrentMonth ? 1 : 0.35,
        backdropFilter: "blur(12px)",
        border: hasEvents
          ? "2px solid rgba(220,38,38,0.95)"
          : dateRangeMode
          ? "2px dashed rgba(59, 130, 246, 0.5)"
          : "1px solid rgba(255,255,255,0.12)",
        borderRadius: isMobile ? "10px" : "18px",
        overflow: "hidden",
        boxShadow:
          hasEvents
            ? "0 6px 22px 0 rgba(220,38,38,0.12)"
            : isSelected || isToday || isVacation
            ? "0 4px 18px 0 rgba(59,130,246,0.10)"
            : "0 1px 4px 0 rgba(0,0,0,0.04)",
        zIndex: isToday ? 2 : 1,
        outline: isToday ? "2px solid #2e7cba" : undefined,
      };
    },

    calendarDayNumber: (isSelected, isToday, isVacation, hasEvents) => ({
      fontSize: isMobile ? "14px" : "18px",
      fontWeight: "800",
      color:
        isSelected || isToday || isVacation || hasEvents
          ? "#fff"
          : theme === "dark"
          ? "#fff"
          : "#1a1a1a",
      marginBottom: isMobile ? "4px" : "10px",
      display: "flex",
      alignItems: "center",
      justifyContent: isRTL ? "flex-start" : "flex-end",
      letterSpacing: "-0.02em",
      textShadow:
        isSelected || isToday || isVacation || hasEvents
          ? "0 2px 8px rgba(0,0,0,0.18)"
          : "none",
      background:
        isSelected || isToday || isVacation || hasEvents
          ? "rgba(0,0,0,0.08)"
          : "transparent",
      borderRadius: isMobile ? "6px" : "8px",
      padding: "0 6px",
      minHeight: isMobile ? "20px" : "28px",
      minWidth: isMobile ? "20px" : "28px",
      width: "fit-content",
      marginLeft: isRTL ? "0" : "auto",
      marginRight: isRTL ? "auto" : "0",
    }),

    eventItem: (event) => ({
      background: `linear-gradient(90deg, ${event.color}CC 60%, ${event.color}99 100%)`,
      color: "#fff",
      padding: isMobile ? "3px 6px" : "5px 10px",
      borderRadius: isMobile ? "6px" : "8px",
      fontSize: isMobile ? "10px" : "12px",
      fontWeight: "700",
      marginBottom: isMobile ? "3px" : "5px",
      cursor: "pointer",
      transition: "all 0.18s cubic-bezier(0.25,0.46,0.45,0.94)",
      display: "flex",
      alignItems: "center",
      gap: isMobile ? "4px" : "6px",
      backdropFilter: "blur(8px)",
      border: `1.5px solid ${event.color}`,
      boxShadow: `0 2px 8px 0 ${event.color}22`,
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      maxWidth: "100%",
      textAlign: isRTL ? "right" : "left",
      flexDirection: isRTL ? "row-reverse" : "row",
    }),

    sidebar: {
      display: "flex",
      flexDirection: "column",
      gap: isMobile ? "16px" : "24px",
      order: isMobile ? -1 : 0,
    },

    // "Upcoming Events" — same WelcomeHeader-style treatment as headerSection
    // / controlPanel above.
    sidebarCard: {
      background:
        theme === "dark"
          ? "linear-gradient(135deg, rgba(30, 41, 59, 0) 0%, rgba(15, 23, 42, 0) 100%)"
          : "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%)",
      padding: isMobile ? "20px 16px" : "28px",
      borderRadius: isMobile ? "16px" : "20px",
      backdropFilter: "blur(20px)",
      WebkitBackdropFilter: "blur(20px)",
      border:
        theme === "dark"
          ? "1px solid rgba(148, 163, 184, 0.15)"
          : "1px solid rgba(0, 0, 0, 0.05)",
      transition: "all 0.3s ease",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      overflowY: "auto",
      boxSizing: "border-box",
    },

    sidebarCard1: {
      background:
        theme === "dark" ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.98)",
      padding: isMobile ? "20px 16px" : "28px",
      borderRadius: isMobile ? "16px" : "20px",
      backdropFilter: "blur(25px)",
      WebkitBackdropFilter: "blur(25px)",
      maxWidth: "100px",
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.15)"
          : "1px solid rgba(0,0,0,0.05)",
      marginBottom: 33,
      boxShadow:
        theme === "dark"
          ? "0 20px 40px rgba(0,0,0,0.4)"
          : "0 20px 40px rgba(0,0,0,0.12)",
      transition: "all 0.3s ease",
    },

    sidebarTitle: {
      fontSize: isMobile ? "16px" : "20px",
      fontWeight: "700",
      color: theme === "dark" ? "#fff" : "#1a1a1a",
      marginBottom: isMobile ? "16px" : "20px",
      display: "flex",
      alignItems: "center",
      gap: "12px",
      flexDirection: isRTL ? "row-reverse" : "row",
      textAlign: isRTL ? "right" : "left",
    },

    actionButton: (variant = "default", size = "medium") => {
      const variants = {
        default: {
          background:
            theme === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
          color: theme === "dark" ? "#fff" : "#1a1a1a",
          border:
            theme === "dark"
              ? "2px solid rgba(255,255,255,0.15)"
              : "2px solid rgba(0,0,0,0.08)",
        },
        primary: {
          background: "linear-gradient(135deg, #2e7cba, #2e7cba)",
          color: "#fff",
          border: "none",
        },
        success: {
          background: "linear-gradient(135deg, #2e7cba, #2e7cba)",
          color: "#fff",
          border: "none",
        },
        warning: {
          background: "linear-gradient(135deg, #f59e0b, #d97706)",
          color: "#fff",
          border: "none",
          boxShadow: "0 8px 25px rgba(245, 158, 11, 0.4)",
        },
        danger: {
          background: "linear-gradient(135deg, #ef4444, #dc2626)",
          color: "#fff",
          border: "none",
          boxShadow: "0 8px 25px rgba(239, 68, 68, 0.4)",
        },
      };

      const sizes = {
        small: {
          padding: isMobile ? "8px 12px" : "10px 16px",
          fontSize: isMobile ? "12px" : "13px",
        },
        medium: {
          padding: isMobile ? "10px 16px" : "14px 24px",
          fontSize: isMobile ? "13px" : "14px",
        },
        large: {
          padding: isMobile ? "14px 20px" : "18px 32px",
          fontSize: isMobile ? "14px" : "16px",
        },
      };

      return {
        ...variants[variant],
        ...sizes[size],
        borderRadius: isMobile ? "10px" : "14px",
        fontWeight: "700",
        cursor: "pointer",
        transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: isMobile ? "8px" : "10px",
        outline: "none",
        letterSpacing: "0.3px",
        textTransform: "uppercase",
        backdropFilter: "blur(15px)",
        position: "relative",
        overflow: "hidden",
        whiteSpace: "nowrap",
        flexDirection: isRTL ? "row-reverse" : "row",
      };
    },

    modalOverlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      // Matches (Admin)EmployeeActions' exact overlay (0.75 opacity, 16px
      // blur) — this page's overlay was darker/blurrier (0.8, 12px... in the
      // wrong direction) than the system standard.
      background: "rgba(0, 0, 0, 0.75)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
      backdropFilter: "blur(16px)",
      padding: isMobile ? "10px" : "20px",
    },

    modalContainer: {
      // Matches (Admin)EmployeeActions' modalContent gradient exactly — the
      // flat rgba(15,23,42,0.95) this used before was noticeably darker
      // ("black") than the system's lighter slate gradient at 0.99 opacity.
      background:
        theme === "dark"
          ? "linear-gradient(135deg, rgba(30, 41, 59, 0.99), rgba(51, 65, 85, 0.99))"
          : "linear-gradient(135deg, rgba(255, 255, 255, 0.99), rgba(248, 250, 252, 0.99))",
      backdropFilter: "blur(30px)",
      WebkitBackdropFilter: "blur(30px)",
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.12)"
          : "1px solid rgba(0,0,0,0.08)",
      borderRadius: isMobile ? "16px" : "24px",
      padding: "0",
      width: "100%",
      maxWidth: isMobile ? "95vw" : "700px",
      maxHeight: isMobile ? "95vh" : "90vh",
      overflow: "hidden",
      boxShadow: "0 32px 64px -12px rgba(0,0,0,0.6)",
      direction: isRTL ? "rtl" : "ltr",
    },

    modalHeader: {
      display: "flex",
      flexDirection: isMobile ? "column" : "row",
      justifyContent: "space-between",
      alignItems: isMobile ? "flex-start" : "center",
      gap: isMobile ? "16px" : "20px",
      padding: isMobile ? "20px 20px 0 20px" : "32px 32px 0 32px",
      marginBottom: isMobile ? "16px" : "24px",
    },

    modalTitleSection: {
      display: "flex",
      alignItems: "center",
      gap: isMobile ? "16px" : "20px",
      flex: 1,
      flexDirection: isRTL ? "row" : "row",
    },

    modalIcon: {
      width: isMobile ? "48px" : "56px",
      height: isMobile ? "48px" : "56px",
      borderRadius: isMobile ? "12px" : "16px",
      background: "linear-gradient(135deg, #2e7cba, #2e7cba)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#fff",
      flexShrink: 0,
    },

    modalTitle: {
      fontSize: isMobile ? "20px" : "28px",
      fontWeight: "800",
      color: theme === "dark" ? "#fff" : "#1a1a1a",
      margin: 0,
      letterSpacing: "-0.01em",
      textAlign: isRTL ? "right" : "left",
    },

    modalSubtitle: {
      fontSize: isMobile ? "13px" : "15px",
      color:
        theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(26,26,26,0.6)",
      margin: "4px 0 0 0",
      fontWeight: "500",
      textAlign: isRTL ? "right" : "left",
    },

    closeButton: {
      background: "none",
      border: "none",
      color:
        theme === "dark" ? "rgba(255,255,255,0.6)" : "rgba(26,26,26,0.6)",
      cursor: "pointer",
      padding: isMobile ? "8px" : "12px",
      borderRadius: isMobile ? "8px" : "12px",
      transition: "all 0.2s ease",
      fontSize: isMobile ? "20px" : "24px",
      fontWeight: "300",
      flexShrink: 0,
    },

    formContainer: {
      padding: isMobile ? "0 16px 16px" : "0 32px 32px",
      maxHeight: isMobile ? "calc(95vh - 120px)" : "calc(90vh - 120px)",
      overflowY: "auto",
      scrollbarWidth: "none",
      msOverflowStyle: "none",
      WebkitOverflowScrolling: "touch",
    },

    formGrid: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))",
      gap: isMobile ? "12px" : "12px",
      alignItems: "start",
      columnGap: isMobile ? "12px" : "16px",
      marginBottom: isMobile ? "12px" : "16px",
    },

    formGroup: {
      marginBottom: isMobile ? "16px" : "20px",
    },

    formLabel: {
      display: "block",
      fontSize: isMobile ? "13px" : "14px",
      fontWeight: "700",
      color: theme === "dark" ? "#fff" : "#1a1a1a",
      marginBottom: "8px",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      textAlign: isRTL ? "right" : "left",
    },

    formInput: {
      width: "100%",
      padding: isMobile ? "8px 12px" : "10px 14px",
      borderRadius: isMobile ? "10px" : "14px",
      border:
        theme === "dark"
          ? "2px solid rgba(255,255,255,0.12)"
          : "2px solid rgba(0,0,0,0.08)",
      background:
        theme === "dark" ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.9)",
      color: theme === "dark" ? "#fff" : "#1a1a1a",
      fontSize: isMobile ? "14px" : "15px",
      fontWeight: "500",
      outline: "none",
      transition: "all 0.3s ease",
      backdropFilter: "blur(10px)",
      boxSizing: "border-box",
      textAlign: isRTL ? "right" : "left",
      minHeight: "40px",
      display: "flex",
      alignItems: "center",
    },

    formInputWrapper: {
      width: "100%",
      minHeight: "40px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },

    formTextarea: {
      width: "100%",
      padding: isMobile ? "12px 16px" : "16px 20px",
      borderRadius: isMobile ? "10px" : "14px",
      border:
        theme === "dark"
          ? "2px solid rgba(255,255,255,0.12)"
          : "2px solid rgba(0,0,0,0.08)",
      background:
        theme === "dark" ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.9)",
      color: theme === "dark" ? "#fff" : "#1a1a1a",
      fontSize: isMobile ? "14px" : "15px",
      fontWeight: "500",
      outline: "none",
      resize: "vertical",
      minHeight: isMobile ? "80px" : "120px",
      fontFamily: "inherit",
      transition: "all 0.3s ease",
      backdropFilter: "blur(10px)",
      boxSizing: "border-box",
      textAlign: isRTL ? "right" : "left",
    },

    formSelect: {
      width: "100%",
      padding: isMobile ? "8px 12px" : "10px 14px",
      borderRadius: isMobile ? "10px" : "14px",
      border:
        theme === "dark"
          ? "2px solid rgba(255,255,255,0.12)"
          : "2px solid rgba(0,0,0,0.08)",
      background:
        theme === "dark" ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.9)",
      color: theme === "dark" ? "#fff" : "#1a1a1a",
      fontSize: isMobile ? "14px" : "15px",
      fontWeight: "500",
      outline: "none",
      cursor: "pointer",
      transition: "all 0.3s ease",
      backdropFilter: "blur(10px)",
      boxSizing: "border-box",
      textAlign: isRTL ? "right" : "left",
    },

    formActions: {
      display: "flex",
      flexDirection: isMobile ? "column" : isRTL ? "row-reverse" : "row",
      gap: isMobile ? "12px" : "16px",
      justifyContent: "flex-end",
      paddingTop: isMobile ? "16px" : "24px",
      borderTop:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.1)"
          : "1px solid rgba(0,0,0,0.08)",
    },

    loadingOverlay: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(0,0,0,0.3)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: isMobile ? "16px" : "24px",
      backdropFilter: "blur(8px)",
      zIndex: 10,
    },

    loadingSpinner: {
      width: isMobile ? "32px" : "48px",
      height: isMobile ? "32px" : "48px",
      border: "4px solid rgba(255,255,255,0.2)",
      borderTop: "4px solid #2e7cba",
      borderRadius: "50%",
      animation: "spin 1s linear infinite",
    },

    emptyState: {
      padding: isMobile ? "40px 16px" : "80px 32px",
      textAlign: "center",
      color: theme === "dark" ? "rgba(255,255,255,0.6)" : "#6b7280",
    },

    emptyIcon: {
      width: isMobile ? "64px" : "96px",
      height: isMobile ? "64px" : "96px",
      margin: "0 auto 16px",
      opacity: 0.6,
      color: theme === "dark" ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)",
    },

    emptyTitle: {
      fontSize: isMobile ? "18px" : "24px",
      fontWeight: "700",
      color: theme === "dark" ? "rgba(255,255,255,0.8)" : "#374151",
      margin: "0 0 12px 0",
    },

    emptyDescription: {
      fontSize: isMobile ? "14px" : "16px",
      opacity: 0.8,
      margin: 0,
      lineHeight: "1.5",
    },

    emptyDescription1: {
      fontSize: isMobile ? "14px" : "16px",
      opacity: 0.8,
      margin: 0,
      marginBottom: "32px",
      lineHeight: "1.5",
    },
  };
};

export default getResponsiveStyles;
